import type { ExpDisplayUpdate, ExpRateChartGap, ExpRateChartSnapshot, SessionStatus } from '@/domain/types'
import { EXP_TRANSITION_REVIEW_MS } from './exp-transition'
import { interpolateExpGain } from './exp-rate-window'
import type { UncommittedExpReview } from './session-exp-counter'

export const EXP_CHART_SPAN_MS = 300_000
export const EXP_CHART_STEP_MS = 10_000
export const EXP_RATE_WINDOW_MS = 60_000
interface SourcePoint { at: number; gain: number | null; provisional: number | null; absolute: number; segment: number; approximate: boolean }

/** Source observations, not timer-generated rates. Revisions rebuild affected windows. */
export class SessionExpHistory {
  private points: SourcePoint[] = []
  private gaps: ExpRateChartGap[] = []
  private openGap: { from: number; reason: ExpRateChartGap['reason'] } | null = null
  private segment = 0
  private origin = 0
  private boundary = -Infinity
  private resolution: string | null = null
  private freshnessMs = 15_000
  private scope: 'session' | 'recovery' = 'session'

  reset(at: number): void {
    this.points = []; this.gaps = []; this.openGap = null
    this.origin = at; this.boundary = at; this.segment = 0; this.resolution = null; this.scope = 'session'
  }

  setSamplingInterval(ms: number): void {
    if (Number.isFinite(ms) && ms > 0) this.freshnessMs = Math.max(15_000, ms * 3)
  }

  breakContinuity(at: number, reason: ExpRateChartGap['reason'] = 'interrupted'): void {
    if (!this.openGap) { this.segment++; this.openGap = { from: at, reason } }
    this.boundary = Math.max(this.boundary, at)
    this.resolution = null
  }

  ingest(display: ExpDisplayUpdate, invalidated = false, scope: 'session' | 'recovery' = 'session', uncommitted?: UncommittedExpReview): void {
    const at = display.sourceAt
    if (at === null || !display.point || !Number.isFinite(at) || at < this.boundary) return
    if (this.scope === 'recovery' && scope === 'session') return
    let last = this.points.at(-1)
    if (last && at < last.at) return
    if (scope !== this.scope) { this.breakContinuity(last?.at ?? at); this.scope = scope }
    // A pending candidate has no new source point. Its presentation snapshot
    // must not replace the previous trusted point with a null earnings value.
    if (!invalidated && uncommitted?.status === 'pending' && at < uncommitted.fromSourceAt) return
    const change = display.journeyTransition
    const key = change ? `${change.id ?? change.fromSourceAt}:${change.revision}:${change.status}:${change.toSourceAt}` : null
    const revised = key !== null && key !== this.resolution
    const discardedCandidate = uncommitted?.status === 'discarded' && change?.status === 'discarded'
      && uncommitted.fromSourceAt === change.fromSourceAt && display.continuity === 'contiguous'
    if (invalidated || revised && (change?.status === 'discarded' && !discardedCandidate || change?.status === 'unavailable'
      || change?.status === 'confirmed' && change.kind === 'correction')) {
      const from = Math.min(change?.fromSourceAt ?? at, last?.at ?? at)
      // Remove the suspect interval so an old false spike cannot survive a correction.
      this.points = this.points.filter(p => p.at < from)
      this.breakContinuity(from, 'pending')
      last = this.points.at(-1)
    }
    if (last && !this.openGap && (at - last.at > this.freshnessMs || display.continuity === 'discontinuous' && !change)) {
      this.breakContinuity(last.at)
    }
    if (this.openGap) {
      this.gaps.push({ ...this.openGap, to: at })
      this.openGap = null
    }
    if (key) this.resolution = key
    const point: SourcePoint = { at, gain: display.netGain
      ?? (change?.status === 'pending' && at < change.fromSourceAt ? display.pendingNetGain ?? null : null),
      provisional: display.pendingNetGain ?? display.netGain, absolute: display.point.absolute, segment: this.segment, approximate: display.approximate }
    if (this.points.at(-1)?.at === at) this.points[this.points.length - 1] = point
    else this.points.push(point)
    if (change?.status === 'pending') {
      for (const p of this.points) if (p.segment === this.segment && p.at >= change.fromSourceAt) p.gain = null
    } else if (change?.status === 'confirmed' && revised) {
      if (change.netOffset == null || !Number.isFinite(change.netOffset)) {
        this.breakContinuity(at, 'pending')
      } else for (const p of this.points) {
        if (p.segment === this.segment && p.at >= change.fromSourceAt && p.at <= change.toSourceAt) {
          p.gain = p.at >= (change.correctionFromSourceAt ?? change.fromSourceAt) ? p.absolute + change.netOffset : p.provisional
          p.approximate = display.approximate
        }
      }
    }
    this.prune(at)
  }

  private prune(now: number): void {
    const cutoff = now - EXP_CHART_SPAN_MS - EXP_RATE_WINDOW_MS - EXP_TRANSITION_REVIEW_MS - this.freshnessMs
    let remove = 0
    while (remove + 1 < this.points.length && this.points[remove + 1]!.at < cutoff) remove++
    if (remove) this.points.splice(0, remove)
    this.gaps = this.gaps.filter(g => g.to >= cutoff)
  }

  private window(at: number, duration: number): { value: number | null; segment: number; approximate: boolean } {
    const start = at - duration
    const a = this.points.findIndex(p => p.at >= start)
    const b = this.points.findIndex(p => p.at >= at)
    const missing = { value: null, segment: -1, approximate: false }
    if (a < 0 || b < 0) return missing
    const left = this.points[a]!.at === start ? a : a - 1
    if (left < 0) return missing
    const entries = this.points.slice(left, b + 1)
    const segment = entries[0]!.segment
    if (entries.some((p, i) => p.gain === null || p.segment !== segment || i > 0 && p.at - entries[i - 1]!.at > this.freshnessMs)) return missing
    const gain = (index: number, time: number) => {
      const right = this.points[index]!, l = right.at === time ? right : this.points[index - 1]!
      return interpolateExpGain(l.at, l.gain!, right.at, right.gain!, time)
    }
    const value = (gain(b, at) - gain(a, start)) * 3_600_000 / duration
    return { value: Number.isFinite(value) ? value : null, segment, approximate: entries.some(p => p.approximate) || this.points[a]!.at !== start || this.points[b]!.at !== at }
  }

  snapshot(now: number, average: number | null, status: SessionStatus = 'running'): ExpRateChartSnapshot {
    this.prune(now)
    const from = now - EXP_CHART_SPAN_MS
    const points: ExpRateChartSnapshot['points'][number][] = []
    for (let at = this.origin + Math.max(0, Math.ceil((from - this.origin) / EXP_CHART_STEP_MS)) * EXP_CHART_STEP_MS;
      at <= now; at += EXP_CHART_STEP_MS) points.push({ at, ...this.window(at, EXP_RATE_WINDOW_MS) })
    const last = this.points.at(-1)
    const stale = last && now - last.at > this.freshnessMs
    const gaps = [...this.gaps]
    if (this.openGap) gaps.push({ ...this.openGap, to: now })
    else if (stale) gaps.push({ from: last.at, to: now, reason: 'interrupted' })
    // Pending points represent genuinely unavailable values, not zero earnings.
    let pendingAt: number | null = null
    for (const p of this.points) {
      if (p.gain === null) pendingAt ??= p.at
      else if (pendingAt !== null) { gaps.push({ from: pendingAt, to: p.at, reason: 'pending' }); pendingAt = null }
    }
    if (pendingAt !== null) gaps.push({ from: pendingAt, to: now, reason: 'pending' })
    // Derive warmup from trusted source runs, including revised history. Timer ticks
    // cannot manufacture coverage, and warmup is not an additional capture outage.
    let runStart: number | null = null
    let previous: SourcePoint | undefined
    let continuousMs = 0
    const finishRun = () => {
      if (runStart === null || !previous) return
      const to = Math.min(previous.at, runStart + EXP_RATE_WINDOW_MS)
      if (to > runStart) gaps.push({ from: runStart, to, reason: 'warming' })
    }
    for (const p of this.points) {
      if (p.gain === null || !previous || previous.gain === null || p.segment !== previous.segment
        || p.at - previous.at > this.freshnessMs) {
        finishRun()
        runStart = p.gain === null ? null : p.at
      }
      continuousMs = runStart === null ? 0 : Math.min(EXP_RATE_WINDOW_MS, p.at - runStart)
      previous = p
    }
    finishRun()
    const state = status === 'paused' ? 'paused' : status === 'interrupted' || stale ? 'stale'
      : status !== 'running' && status !== 'ended' ? 'unavailable' : last?.gain === null ? 'pending'
        : this.openGap || !last ? 'warming' : 'ready'
    const recentWindow = state === 'ready' && last ? this.window(last.at, EXP_RATE_WINDOW_MS) : null
    const value = recentWindow?.value ?? null
    return { from, to: now, points, gaps: gaps.filter(g => g.to >= from && g.from <= now),
      average: average !== null && Number.isFinite(average) ? average : null,
      recent: { value, at: last?.at ?? null, windowMs: EXP_RATE_WINDOW_MS, continuousMs: this.openGap ? 0 : continuousMs,
        approximate: recentWindow?.approximate ?? false, windowStartAt: value !== null && last ? last.at - EXP_RATE_WINDOW_MS : null, status: state === 'ready' && value === null ? 'warming' : state } }
  }
}
